import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, ShieldCheck, Users, TrendingUp, Network, Wrench, IndianRupee, Leaf, Sun, HardHat, Truck, Briefcase, MapPin, FileCheck2, Timer, Share2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { Disclaimer, OperatorBlock } from "../components/Layout";
import { CommissionExample, CtaLink, FaqList } from "../components/Blocks";
import { ToshniwalProfile } from "../components/BrandProfiles";
import { PlanCards } from "./Plans";
import { publicApi } from "../lib/api";
import { useSite } from "../lib/site";
import { useT } from "../lib/i18n";

const AUDIENCE = [Sun, HardHat, Truck, Briefcase, MapPin];

const BENEFITS = [Handshake, ShieldCheck, Users, TrendingUp, Network, Wrench, IndianRupee, Leaf];
const STEPS = [0, 1, 2, 3, 4, 5, 6];

function Hero() {
  const { t } = useT();
  return (
    <section className="relative overflow-hidden bg-[#0B1B3D] text-white">
      <img src="/images/rooftop-solar-panel-array-1600.webp" srcSet="/images/rooftop-solar-panel-array-800.webp 800w, /images/rooftop-solar-panel-array-1600.webp 1600w" sizes="100vw" alt={t("home.heroAlt")} className="absolute inset-0 h-full w-full object-cover opacity-40" fetchPriority="high" width="1600" height="1067" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3D] via-[#0B1B3D]/90 to-[#0B1B3D]/40" />
      <div className="relative container-x py-20 sm:py-28">
        <p className="font-mono text-xs tracking-widest uppercase text-amber-400 mb-4 reveal">{t("home.eyebrow")}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-3xl reveal" data-testid="home-h1">{t("home.h1")}</h1>
        <p className="mt-6 text-base sm:text-lg text-slate-200 max-w-2xl reveal" style={{ animationDelay: "80ms" }}>{t("home.sub")}</p>
        <div className="mt-10 flex flex-wrap gap-4 reveal" style={{ animationDelay: "160ms" }}>
          <CtaLink to="/become-a-solar-partner" testid="hero-explore-btn">{t("home.explore")}</CtaLink>
          <Link to="/how-it-works" className="inline-flex items-center font-semibold py-3 px-6 rounded-lg border-2 border-white/70 hover:bg-white hover:text-slate-900 transition-colors" data-testid="hero-how-btn">{t("home.viewHow")}</Link>
        </div>
        <p className="mt-8 text-xs text-slate-300 max-w-xl">{t("common.noGuarantee")}</p>
      </div>
    </section>
  );
}

const Head = ({ eyebrow, title, children }) => (<div className="mb-10 max-w-3xl"><p className="eyebrow">{eyebrow}</p><h2 className="h2 mt-2">{title}</h2>{children && <p className="text-slate-700 mt-3">{children}</p>}</div>);

function Audience() {
  const { t } = useT();
  const items = t("home.audience");
  return (
    <section className="container-x section-y">
      <Head eyebrow={t("home.audEyebrow")} title={t("home.audTitle")}>{t("home.audSub")}</Head>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">{AUDIENCE.map((Icon, i) => <div key={i} className="rounded-xl border bg-white p-5" data-testid={`audience-${i}`}><Icon className="h-6 w-6 text-[#0B1B3D]" /><h3 className="font-semibold mt-3">{items[i][0]}</h3><p className="text-sm text-slate-600 mt-1">{items[i][1]}</p></div>)}</div>
    </section>
  );
}

function Benefits() {
  const { t } = useT();
  const items = t("home.benefits");
  return (
    <section className="bg-white border-y section-y">
      <div className="container-x">
        <Head eyebrow={t("home.benEyebrow")} title={t("home.benTitle")}>{t("home.benSub")}</Head>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="benefit-cards">
          {BENEFITS.map((Icon, i) => (
            <div key={i} className="rounded-2xl border p-6 hover:border-amber-500 hover:-translate-y-0.5 transition-[transform,border-color] duration-200" data-testid={`benefit-card-${i}`}>
              <span className="grid place-items-center h-11 w-11 rounded-full bg-[#0B1B3D] text-amber-400"><Icon className="h-5 w-5" /></span>
              <h3 className="font-semibold text-lg mt-4">{items[i][0]}</h3><p className="text-sm text-slate-600 mt-2 leading-relaxed">{items[i][1]}</p>
            </div>
          ))}
        </div>
        <div className="mt-12"><ToshniwalProfile compact /></div>
      </div>
    </section>
  );
}

function Process() {
  const { t } = useT();
  return (
    <section className="container-x section-y">
      <Head eyebrow={t("home.procEyebrow")} title={t("home.procTitle")} />
      <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">{STEPS.map((i) => <li key={i} className="rounded-xl border bg-white p-5" data-testid={`process-step-${i}`}><span className="font-mono text-sm font-bold text-amber-700">0{i + 1}</span><p className="mt-2 text-slate-800">{t("home.steps")[i]}</p></li>)}</ol>
      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <div className="rounded-xl border bg-white p-5"><span className="badge-shared"><Share2 className="h-3 w-3" />{t("home.shared")}</span><p className="mt-3 text-sm text-slate-700">{t("home.sharedText")}</p></div>
        <div className="rounded-xl border bg-white p-5"><span className="badge-exclusive"><Timer className="h-3 w-3" />{t("home.exclusive")}</span><p className="mt-3 text-sm text-slate-700">{t("home.exclusiveText")}</p></div>
      </div>
    </section>
  );
}

function Packages() {
  const [plans, setPlans] = useState([]);
  const { t } = useT();
  useEffect(() => { publicApi.get("/public/plans").then((r) => setPlans(r.data)); }, []);
  return (
    <section className="bg-slate-50 border-y section-y">
      <div className="container-x">
        <Head eyebrow={t("home.pkgEyebrow")} title={t("home.pkgTitle")} />
        <div className="mb-8 max-w-3xl"><Disclaimer testid="home-draft-banner">{t("common.draftNote")}</Disclaimer></div>
        <PlanCards plans={plans} />
        <div className="mt-8"><CtaLink to="/partner-plans" variant="outline" testid="home-compare-btn">{t("home.compare")}</CtaLink></div>
        <div className="grid lg:grid-cols-2 gap-10 items-center mt-16">
          <div><h3 className="text-2xl font-bold">{t("home.comTitle")}</h3><p className="text-slate-700 mt-3">{t("home.comText")}</p></div>
          <CommissionExample />
        </div>
      </div>
    </section>
  );
}

function Trust() {
  const { t } = useT();
  return (
    <section className="container-x section-y grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5"><p className="eyebrow">{t("home.trustEyebrow")}</p><h2 className="h2 mt-2">{t("home.trustTitle")}</h2><p className="text-slate-700 mt-3">{t("home.trustSub")}</p></div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {t("home.trust").map(([a, d]) => <div key={a} className="rounded-xl border bg-white p-5"><FileCheck2 className="h-5 w-5 text-emerald-700" /><h3 className="font-semibold mt-2">{a}</h3><p className="text-sm text-slate-600 mt-1">{d}</p></div>)}
        <div className="sm:col-span-2 rounded-xl border bg-white p-5"><OperatorBlock /></div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t } = useT();
  return (
    <div data-testid="home-page">
      <Seo title={t("home.seoTitle")} description={t("home.seoDesc")} />
      <Hero /><Audience /><Benefits /><Process /><Packages /><Trust />
      <section className="container-x pb-16 max-w-4xl"><h2 className="h2 mb-6">{t("home.faqTitle")}</h2><FaqList items={t("faq")} /></section>
      <section className="bg-[#0B1B3D] text-white">
        <div className="container-x py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">{t("home.finalTitle")}</h2>
          <div className="flex flex-wrap gap-3"><CtaLink to="/apply" testid="final-apply-btn">{t("nav.apply")}</CtaLink><Link to="/contact" className="inline-flex items-center font-semibold py-3 px-6 rounded-lg border-2 border-white hover:bg-white hover:text-slate-900 transition-colors" data-testid="final-contact-btn">{t("home.contactUs")}</Link></div>
        </div>
      </section>
    </div>
  );
}
